import { FC, MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Column } from '@material-table/core';
import DataTable from '../../components/shared/DataTable/DataTable';
import Badge from '../../components/shared/Badge/Badge';
import { SupplierSummary } from '../../services/service-evaluations';
import { EvaluationContextType } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import { requestStatusMeta } from '../../util/evaluationStatusMeta';
import { formatDayKey } from './reviewFormat';
import { isFromLink } from './rowClick';
import TagChips from './TagChips';
import styles from './page-supplier-evaluations.module.scss';

export type EvaluationRow = SupplierSummary['requests'][number];

type Props = {
  requests: EvaluationRow[];
  statusUrlOf: (requestId: string) => string;
};

const TABULAR = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const nameOf = (request: EvaluationRow) => {
  if (request.contextType === EvaluationContextType.Project) {
    const name = request.projectName ?? '';
    return request.projectNumber ? `${name} (${request.projectNumber})` : name;
  }
  if (request.contextType === EvaluationContextType.Historical) {
    const { projectName: name, projectNumber: number } = request;
    return name && number ? `${name} (${number})` : name || number || request.title;
  }
  return request.title;
};

const EvaluationTable: FC<Props> = ({ requests, statusUrlOf }) => {
  const [t, i18n] = useTranslation('evaluations');
  const history = useHistory();

  const columns: Column<EvaluationRow>[] = useMemo(
    () => [
      {
        title: t('review.evaluations.name', 'Utvärdering'),
        field: 'title',
        customSort: (a, b) => nameOf(a).localeCompare(nameOf(b), 'sv'),
        render: request => (
          <>
            <Link className={styles.rowLink} to={statusUrlOf(request.requestId)}>
              {nameOf(request)}
            </Link>
            <TagChips tags={request.tags} />
          </>
        ),
      },
      {
        title: t('review.evaluations.sentDate', 'Skickad'),
        field: 'sentDate',
        width: 160,
        cellStyle: TABULAR,
        render: request => formatDayKey(request.sentDate, i18n.language),
      },
      {
        title: t('review.evaluations.sent', 'Skickade'),
        field: 'sentCount',
        width: 96,
        cellStyle: TABULAR,
        render: request => request.sentCount,
      },
      {
        title: t('review.evaluations.answered', 'Besvarade'),
        field: 'answeredCount',
        width: 96,
        cellStyle: TABULAR,
        render: request => request.answeredCount,
      },
      {
        title: t('review.evaluations.score', 'Betyg'),
        field: 'score',
        width: 96,
        cellStyle: TABULAR,
        render: request => <span className={styles.barValue}>{formatScore(request.score)}</span>,
      },
      {
        title: t('review.evaluations.status', 'Status'),
        field: 'status',
        width: 140,
        render: request => {
          const meta = requestStatusMeta(request.status);
          return (
            <Badge color={meta.color} backgroundColor={meta.backgroundColor}>
              {t(meta.labelKey, meta.fallback)}
            </Badge>
          );
        },
      },
    ],
    [t, i18n.language, statusUrlOf],
  );

  const rows = useMemo(() => requests.map(request => ({ ...request })), [requests]);

  const openRow = (event?: MouseEvent, request?: EvaluationRow) => {
    if (!request || isFromLink(event)) return;
    history.push(statusUrlOf(request.requestId));
  };

  return (
    <DataTable<EvaluationRow>
      isLoading={false}
      columns={columns}
      data={rows}
      onRowClick={openRow}
    />
  );
};

export default EvaluationTable;
